import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

export default function SectionNav({ sections, current, onSelect, onPrev, onNext }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 w-full max-w-[calc(100%-1.5rem)] sm:max-w-xl px-2">
      {/* Pill nav */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-1.5 bg-[#181818]/95 backdrop-blur-xl border border-white/10 rounded-full px-2 py-2 shadow-2xl w-full"
      >
        {/* Prev */}
        <button
          onClick={onPrev}
          disabled={current === 0}
          className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center border border-white/10 text-white/40 hover:text-white hover:border-white/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all duration-200"
        >
          <SafeIcon name="FiChevronLeft" className="h-4 w-4" />
        </button>

        {/* Dots (desktop) / Label tap (mobile) */}
        <div className="flex-1 flex items-center justify-center gap-1.5 overflow-hidden">
          {/* Mobile: show label + tap to open menu */}
          <button
            className="sm:hidden flex items-center gap-2 px-3 py-1 rounded-full bg-brand-teal/15 border border-brand-teal/30 text-brand-teal text-[11px] font-bold uppercase tracking-widest"
            onClick={() => setOpen(!open)}
          >
            <span className="truncate max-w-[120px]">{sections[current].label}</span>
            <SafeIcon name={open ? 'FiChevronUp' : 'FiChevronDown'} className="h-3 w-3 flex-shrink-0" />
          </button>

          {/* Desktop: dots */}
          <div className="hidden sm:flex items-center gap-1.5">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => onSelect(i)}
                title={s.label}
                className="relative flex items-center justify-center transition-all duration-300"
              >
                {i === current ? (
                  <motion.div
                    layoutId="active-dot"
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-teal/20 border border-brand-teal/40"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" />
                    <span className="text-brand-teal text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                      {s.label}
                    </span>
                  </motion.div>
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-white/20 hover:bg-white/50 transition-colors duration-200" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Next */}
        <button
          onClick={onNext}
          disabled={current === sections.length - 1}
          className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center border border-white/10 text-white/40 hover:text-white hover:border-white/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all duration-200"
        >
          <SafeIcon name="FiChevronRight" className="h-4 w-4" />
        </button>
      </motion.div>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="sm:hidden absolute bottom-full mb-2 left-0 right-0 bg-[#181818]/98 backdrop-blur-xl border border-white/10 rounded-2xl p-2 shadow-2xl"
          >
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => { onSelect(i); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  i === current
                    ? 'bg-brand-teal/15 border border-brand-teal/25 text-brand-teal'
                    : 'text-white/50 hover:bg-white/5 hover:text-white border border-transparent'
                }`}
              >
                <span className="text-[10px] font-black uppercase tracking-widest w-6 text-center opacity-40">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-xs font-bold uppercase tracking-widest">{s.label}</span>
                {i === current && (
                  <SafeIcon name="FiCheck" className="h-3 w-3 ml-auto" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}